import React, { useCallback } from 'react';
import { Separator, Stack } from '@fluentui/react';
import { DialogCardError } from 'common/components/Cards/DialogCard/DialogCardError';
import { usePanelMobileView } from 'common/components/Panel/usePanelMobileView';
import { ModalShimmer } from 'common/components/Shimmer/DefaultShimmers/ModalShimmer';
import { Heading4, Text } from 'common/components/Text';
import { pageDataService } from 'common/utilities/PageDataService';
import { useLocalizedStrings } from 'i18n';
import { FamilyMember, FamilyMemberNotificationTopicSettingResponse, NotificationChannel } from 'mcsclient/models';
import { isApiCallComplete, isApiCallFailed } from 'store/apiCallResultStatus';
import { useAppSelector } from 'store/hooks';
import { getFamilyMemberNotificationSetting } from 'store/notifications/notificationSelectors';
import { useNotificationsDispatch, useNotificationSettingsSummary } from 'store/notifications/useNotificationSettings';

import { stackTokens, useNotificationSettingsStyles } from '../NotificationSettings.styles';
import { NotificationSettingsSummary } from '../NotificationSettingsSummary';

import { FamilyMemberView } from './FamilyMemberView';

interface IFamilyMemberNotificationSettingsProps {
  member: FamilyMember;
}

export const FamilyMemberNotificationSettings = ({ member }: IFamilyMemberNotificationSettingsProps) => {
  const { t } = useLocalizedStrings();
  const { notificationSettingsSummaryLoadStatus } = useNotificationSettingsSummary();
  const memberNotificationSettings = useAppSelector((state) => getFamilyMemberNotificationSetting(state.notifications, member.puid!));
  const { updateFamilyMemberNotificationTopicSettings } = useNotificationsDispatch();
  const { userPrincipalName } = pageDataService.getUserData();
  const { isMobileView } = usePanelMobileView();
  const classNames = useNotificationSettingsStyles();

  const onToggleDelegate = useCallback(
    (channel: NotificationChannel, topicId: string) => (checked?: boolean) => {
      updateFamilyMemberNotificationTopicSettings({ memberUserId: member.puid!, channel, topicId, isEnabled: !!checked });
    },
    [member.puid, updateFamilyMemberNotificationTopicSettings]
  );

  const getToggleAriaLabel = useCallback(
    (channel: NotificationChannel, topicName: string) => {
      switch (channel) {
        case 'Push':
          return t('OfficeShellPanel.NotificationSettings.Sections.FamilyMember.Push.ToggleTopicAriaLabel', { topicName });
        case 'Email':
          return t('OfficeShellPanel.NotificationSettings.Sections.FamilyMember.Email.ToggleTopicAriaLabel', { topicName });
        default:
          return undefined;
      }
    },
    [t]
  );

  const onRenderSection = useCallback(
    (channel: NotificationChannel, heading: string, text: string, settings: FamilyMemberNotificationTopicSettingResponse[]) => {
      return (
        <Stack tokens={isMobileView ? stackTokens.gapMD : stackTokens.gapSM}>
          <Stack
            tokens={isMobileView ? stackTokens.gapXS : stackTokens.gapMD}
            className={classNames.familyMemberNotificationSettingSectionText}
          >
            <Heading4 variant={isMobileView ? 'Caption2Strong' : 'Title2Strong'} block>
              {heading}
            </Heading4>
            <Text variant={isMobileView ? 'Caption2' : 'Body1'} block>
              {text}
            </Text>
          </Stack>
          <Stack tokens={isMobileView ? undefined : stackTokens.gapSM}>
            {settings.map((topicSetting) => (
              <NotificationSettingsSummary
                key={`${channel}-${topicSetting.topicId}`}
                className={classNames.notificationSetting}
                notificationType="FamilyMember"
                telemetryProps={{ familyMemberRole: member.role, channel, topicId: topicSetting.topicId }}
                title={isMobileView ? topicSetting.topicName : undefined}
                toggleNotificationLabel={isMobileView ? '' : topicSetting.topicName}
                toggleAriaLabel={getToggleAriaLabel(channel, topicSetting.topicName)}
                notificationsEnabled={topicSetting.isEnabled ?? false}
                onToggleNotification={onToggleDelegate(channel, topicSetting.topicId)}
                disabledReason={topicSetting.disabledReason}
              />
            ))}
          </Stack>
        </Stack>
      );
    },
    [
      isMobileView,
      classNames.familyMemberNotificationSettingSectionText,
      classNames.notificationSetting,
      member.role,
      getToggleAriaLabel,
      onToggleDelegate,
    ]
  );

  const isLoading = !isApiCallComplete(notificationSettingsSummaryLoadStatus);
  if (isLoading) {
    return <ModalShimmer />;
  }
  if (isApiCallFailed(notificationSettingsSummaryLoadStatus)) {
    return (
      <DialogCardError
        title={t('OfficeShellPanel.NotificationSettings.Error.Title')}
        subtitle={t('OfficeShellPanel.NotificationSettings.Error.Subtitle')}
      />
    );
  }

  return (
    <Stack tokens={isMobileView ? stackTokens.gapXXL : stackTokens.gapMD}>
      <FamilyMemberView member={member} className={classNames.noPadding} />
      {!isMobileView && <Separator className={classNames.noPadding} />}
      <Stack tokens={isMobileView ? stackTokens.gapXXL : stackTokens.gapXL}>
        {onRenderSection(
          'Push',
          t('OfficeShellPanel.NotificationSettings.Sections.FamilyMember.Push.Title'),
          t('OfficeShellPanel.NotificationSettings.Sections.FamilyMember.Push.Subtitle'),
          memberNotificationSettings?.pushNotifications ?? []
        )}
        {onRenderSection(
          'Email',
          t('OfficeShellPanel.NotificationSettings.Sections.FamilyMember.Email.Title'),
          t('OfficeShellPanel.NotificationSettings.Sections.FamilyMember.Email.Subtitle', {
            emailAddress: userPrincipalName,
          }),
          memberNotificationSettings?.emailNotifications ?? []
        )}
      </Stack>
    </Stack>
  );
};
